import {
  CaptureErrorLevel,
  Logger,
  LoggerRole,
  LoggerUserContext,
} from '../../logger';

/**
 * Sentry Scope 어댑터 인터페이스.
 * withScope 콜백에서 제공되는 scope 객체가 제공해야 하는 최소 API.
 */
export interface SentryScopeLike {
  setFingerprint(fingerprint: string[]): void;
  setLevel(level: CaptureErrorLevel): void;
  setTag(key: string, value: string | number | boolean): void;
  setExtras(extras: Record<string, unknown>): void;
}

/**
 * Sentry SDK 어댑터 인터페이스.
 *
 * core-logger가 @sentry/nextjs에 직접 의존하지 않도록 SDK를 생성자 주입합니다.
 * 앱별 logger.ts 조립 파일에서 실제 Sentry SDK 인스턴스를 주입합니다.
 */
export interface SentryLike {
  captureException(
    error: unknown,
    options?: { extra?: Record<string, unknown> },
  ): void;
  withScope(callback: (scope: SentryScopeLike) => void): void;
  setContext(name: string, context: Record<string, unknown> | null): void;
  setUser(user: Record<string, unknown> | null): void;
  setTag(key: string, value: string | number | boolean): void;
}

/**
 * Sentry 로거 어댑터.
 *
 * 앱별 logger.ts 조립 파일에서 인스턴스를 생성해 사용합니다.
 * SDK 인스턴스는 생성자 주입 (core-logger가 @sentry/nextjs에 의존하지 않음).
 *
 * role 기본값은 'error' (tracking/debug 이벤트는 수신하지 않음).
 * 예: new SentryLogger(Sentry, { role: 'error' })
 */
export class SentryLogger implements Logger {
  public name = 'sentry';
  public role: LoggerRole;
  private _sentry: SentryLike;

  /**
   * attributes 중 이 allowlist 키들은 자동으로 Sentry tag로 승격됩니다.
   * 나머지는 extra로 전달됩니다.
   */
  private static readonly TAG_ALLOWLIST = [
    'feature',
    'action',
    'errorType',
    'flow',
  ];

  constructor(sentry: SentryLike, options?: { role?: LoggerRole }) {
    this._sentry = sentry;
    this.role = options?.role ?? 'error';
  }

  log: Logger['log'] = () => {
    /* no-op: Sentry는 비즈니스 트래킹 이벤트를 수신하지 않음 */
  };

  debug: Logger['debug'] = () => {
    /* no-op: 현재 정책은 에러만 캡처. 향후 breadcrumb 도입 시 확장 */
  };

  setState: Logger['setState'] = state => {
    if (state) {
      this._sentry.setContext('zep', state as Record<string, unknown>);
    }
  };

  captureError: Logger['captureError'] = (error, options) => {
    const { fingerprint, level, tags, extra, attributes } = options ?? {};
    const hasScopeWork = Boolean(fingerprint || level || tags || attributes);

    if (!hasScopeWork) {
      this._sentry.captureException(error, { extra });
      return;
    }

    this._sentry.withScope(scope => {
      if (fingerprint) {
        scope.setFingerprint(
          Array.isArray(fingerprint) ? fingerprint : [fingerprint],
        );
      }
      if (level) {
        scope.setLevel(level);
      }
      if (tags) {
        for (const [k, v] of Object.entries(tags)) {
          scope.setTag(k, v);
        }
      }

      const mergedExtra: Record<string, unknown> = { ...(extra ?? {}) };
      if (attributes) {
        for (const [k, v] of Object.entries(attributes)) {
          if (
            SentryLogger.TAG_ALLOWLIST.includes(k) &&
            (typeof v === 'string' ||
              typeof v === 'number' ||
              typeof v === 'boolean')
          ) {
            scope.setTag(k, v);
          } else {
            mergedExtra[k] = v;
          }
        }
      }
      if (Object.keys(mergedExtra).length > 0) {
        scope.setExtras(mergedExtra);
      }
      this._sentry.captureException(error);
    });
  };

  setTag: Logger['setTag'] = (key, value) => {
    this._sentry.setTag(key, value);
  };

  setUserContext({
    userId,
    properties,
    integrations,
  }: LoggerUserContext): void {
    const sentryUser = getSentryUser(integrations, properties, userId);
    this._sentry.setUser(sentryUser);
  }

  clearUserContext(): void {
    this._sentry.setUser(null);
  }

  identify(_userId: string): void {
    // Sentry user는 setUserContext()에서 평탄화된 user context를 받아 설정합니다.
    // identify()는 PostHog(Zetta) 식별 전용 fallback 경로이므로 여기서는 no-op.
  }
}

const getSentryUser = (
  integrations: LoggerUserContext['integrations'],
  properties: Record<string, unknown>,
  userId: string,
): Record<string, unknown> => {
  const sentryUser = integrations?.sentry;
  if (sentryUser && typeof sentryUser === 'object') {
    return sentryUser as Record<string, unknown>;
  }

  return {
    ...properties,
    id: String(properties.userId ?? userId),
  };
};
